import { useEffect } from 'react';
import {
    Text, Card,
    Stack, Box,
    Grid,
    RingProgress,
    Flex,
    Group
} from '@mantine/core';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTaskCounts } from "../Settings/store/taskSlice";
import { translate } from "../../utils/i18n";

const DashboardCard = ({ type = 'total', dragHandle }) => {
    const dispatch = useDispatch();
    const { taskCount } = useSelector((state) => state.settings.task);
    const { xWpNonce } = useSelector((state) => state.auth.session);

    useEffect(() => {
        if (!xWpNonce) return;

        if (!taskCount || Object.keys(taskCount).length === 0) {
            dispatch(fetchTaskCounts());
        }
    }, [dispatch, xWpNonce]); // removed taskCount from dependency to avoid infinite loop if it changes

    const getCardConfig = (type) => {
        switch (type) {
            case 'progress':
                return {
                    title: translate('Tasks In Progress'),
                    count: taskCount?.pending_tasks || 0,
                    percent: taskCount?.pending_tasks_percentage || 0,
                    bg: '#DBE5FF',
                    dataKey: 'active_tasks',
                    totalKey: 'pending_tasks'
                };
            case 'completed':
                return {
                    title: translate('Tasks Completed'),
                    count: taskCount?.completed_tasks || 0,
                    percent: taskCount?.completed_tasks_percentage || 0,
                    bg: '#E7F4E8',
                    dataKey: 'completed_tasks',
                    totalKey: 'completed_tasks'
                };
            case 'overdue':
                return {
                    title: translate('Overdue Tasks'),
                    count: taskCount?.overdue_tasks || 0,
                    percent: taskCount?.overdue_tasks_percentage || 0,
                    bg: '#F9D9E0',
                    dataKey: 'overdue_tasks',
                    totalKey: 'overdue_tasks'
                };
            case 'total':
            default:
                return {
                    title: translate('Total Tasks'),
                    count: taskCount?.total_tasks || 0,
                    percent: taskCount?.total_tasks_percentage || 0,
                    bg: '#FCEBE0',
                    dataKey: 'total_tasks',
                    totalKey: 'total_tasks'
                };
        }
    };

    const config = getCardConfig(type);

    return (
        <Card
            radius="md"
            withBorder
            padding="md"
            bg={config.bg}
            h={'100%'}
            pos="relative"
        >
            <Box pos="absolute" top={5} right={5} style={{ zIndex: 10 }}>
                {dragHandle}
            </Box>
            <Grid columns={12}>
                <Grid.Col span={6}>
                    <Flex justify="flex-start" align="center" h="100%">
                        <Stack gap="xs" justify="space-between" h="100%">
                            <Box w="100%" className={dragHandle ? "draggable-header cursor-grab" : ""}>
                                <Text size="xl" c="black" fw={500} lineClamp={1}>
                                    {config.title}
                                </Text>
                                <Box>
                                    <Text style={{ fontSize: '32px' }} c="black" fw={600}>
                                        {config.count}
                                    </Text>
                                    <Text size="sm" c="#6A6A6A" mt={2}>
                                        {translate('%d projects').replace('%d', taskCount && taskCount.projectsData && taskCount.projectsData.length || 0)}
                                    </Text>
                                </Box>
                            </Box>
                        </Stack>
                    </Flex>
                </Grid.Col>
                <Grid.Col span={6}>
                    <Flex justify="flex-end" align="center" h="100%">
                        <RingProgress
                            size={120}
                            thickness={15}
                            roundCaps
                            sections={
                                taskCount && taskCount.projectsData && taskCount.projectsData.length > 0 ? taskCount.projectsData.map((project, index) => ({
                                    value: (project[config.dataKey] / (taskCount[config.totalKey] || 1)) * 100, // Avoid division by zero
                                    color: project.color || '#39758D',
                                    tooltip: project.project_name + ' (' + project[config.dataKey] + ' Tasks)'
                                })) : [
                                    { value: 100, color: '#39758D' }
                                ]
                            }
                            label={
                                <Text c="black" fw={500} ta="center" size="sm">
                                    {config.percent}%
                                </Text>
                            }
                            rootColor="white"
                        />
                    </Flex>
                </Grid.Col>
            </Grid>
        </Card>
    );
};

export default DashboardCard;

